<template>
    <modal
      name="addon-confirmation"
      :adaptive="true"
      :scrollable="true"
      :reset="true"
      width="500px"
      height="auto"
      classes="modal-container"
      :click-to-close="false"
      :esc-to-close="true"
    >
      <div class="bg-white p-6 rounded-lg">
        <h3 class="text-lg text-center font-semibold mb-2">Update {{ addon.name }} Addon</h3>
  
        <div class="mb-5">
          <div class="rounded space-y-3">
            <div v-if="loadingInvoice" class="text-center">
              <div class="animate-spin rounded-full h-6 w-6 mx-auto"></div>
              <p class="text-sm text-gray-500 mt-2">Calculating invoice...</p>
            </div>
            
            <div v-else-if="invoiceError" class="text-center">
              <p class="text-red-500 text-sm">{{ invoiceError }}</p>
              <button 
                @click="fetchInvoiceDetails" 
                class="mt-2 text-blue-600 text-sm hover:underline"
              >
                Try Again
              </button>
            </div>     
            <template v-else>
              <div v-if="invoiceDetails?.line_items?.length > 0" class="pt-2 text-sm">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th class="px-2 py-1 text-left text-xs font-medium text-white uppercase tracking-wider">Description</th>
                      <th class="px-2 py-1 text-right text-xs font-medium text-white uppercase tracking-wider">Amount</th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="(item, index) in invoiceDetails.line_items" :key="index">
                      <td class="px-2 py-1 whitespace-nowrap">
                        <div class="text-sm text-gray-900">{{ item.description }}</div>
                        <div v-if="item.proration" class="text-xs text-gray-500">
                          Prorated for {{ Math.ceil((item.period.end - item.period.start) / 86400) }} days
                        </div>
                      </td>
                      <td class="px-2 py-1 whitespace-nowrap text-right text-sm font-medium">
                        {{ addon.currency || '$' }} {{ (item.amount / 100).toFixed(2) }}
                      </td>
                    </tr>
                    <tr class="border-t-2 border-gray-200 font-medium bg-gray-50">
                      <td class="px-2 py-2 whitespace-nowrap text-right text-sm font-semibold text-gray-900" colspan="2">
                        Total Amount Due: {{ invoiceDetails.currency || '$' }} {{ (invoiceDetails.amount_due / 100).toFixed(2) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </template>
          </div>
        </div>
  
        <div v-if="waitingForPayment" class="mb-4 text-yellow-600 font-medium text-center">
          <span class="text-sm text-red-600">Processing payment—awaiting confirmation. Don’t close this tab.</span>
          <div class="text-sm text-gray-600 mt-2">
            Time left: <span class="font-mono">{{ countdownDisplay }}</span>
          </div>
        </div>
        <div v-if="!waitingForPayment" class="mb-4   text-sm text-red-600 font-medium text-center">
         Note: Complete the payment on the next page, then return here.
        </div>
  
        <div class="flex justify-end space-x-3">
          <button
            v-if="!waitingForPayment"
            @click="$modal.hide('addon-confirmation')"
            class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            v-if="!waitingForPayment"
            @click="confirmPurchase"
            class="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700"
            :disabled="loading"
          >
            <span v-if="!loading">Confirm</span>
            <span v-else>Processing...</span>
          </button>
          <button
            v-if="waitingForPayment"
            @click="forceClose"
            class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close Anyway
          </button>
        </div>
      </div>
    </modal>
  </template>
  
  <script>
  import axios from 'axios';
  export default {
    name: 'AddonConfirmation',
  
    data() {
      return {
        loading: false,
        addon: {
          name: '',
          description: '',
          amount: 0,
          recurring: '',
          quantity: 1,
          existingQuantity: 0,
          totalQuantity: 1,
          stripe_price_id: '',
          isUpdate: false,
          subscriptionId: null,
          pricePerUnit: 0,
          prorationInfo: null
        },
        onConfirm: null,
        waitingForPayment: false,
        paymentCheckInterval: null,
        paymentCheckTimeout: null,
        countdown: 15 * 60, // seconds (15 minutes)
        countdownInterval: null,
        paymentStatus: null,
        invoiceDetails: null,
        loadingInvoice: false,
        invoiceError: null
      };
    },
  
    computed: {
      countdownDisplay() {
        const min = Math.floor(this.countdown / 60).toString().padStart(2, '0');
        const sec = (this.countdown % 60).toString().padStart(2, '0');
        return `${min}:${sec}`;
      }
    },
  
    methods: {
      async show(plan, callback) {
        this.addon = { ...this.addon, ...plan };
        this.onConfirm = callback;
        this.invoiceDetails = null;
        this.invoiceError = null;
        
        // Show the modal immediately
        this.$modal.show('addon-confirmation');
        
        // Then fetch the invoice details
        await this.fetchInvoiceDetails();
      },
      
      async fetchInvoiceDetails() {
        if (!this.addon.stripe_price_id || !this.addon.quantity) {
          this.invoiceError = 'Missing required information to calculate invoice';
          return;
        }
        
        this.loadingInvoice = true;
        this.invoiceError = null;
        
        try {
          const response = await axios.post('/subscription/addon/calculate', {
            price_id: this.addon.stripe_price_id,
            quantity: this.addon.existingQuantity + this.addon.quantity,
            subscription_id: this.addon.isUpdate ? this.addon.subscriptionId : null
          });
          
          if (response.data.success) {
            this.invoiceDetails = response.data;
            
            // Update the addon with the calculated price per unit
            if (this.addon.quantity > 0) {
              this.addon.pricePerUnit = response.data.amount_due / this.addon.quantity / 100; // Convert back to dollars
            }
          } else {
            this.invoiceError = response.data.error || 'Failed to calculate invoice amount';
          }
        } catch (error) {
          console.error('Error fetching invoice details:', error);
          const errorMessage = error.response?.data?.error || 
                             error.message || 
                             'Failed to calculate invoice amount';
          this.invoiceError = errorMessage;
        } finally {
          this.loadingInvoice = false;
        }
      },
      
      hide() {
        // Close all modals
        this.$modal.hide('addon-confirmation');
        this.$modal.hide('module-access-overlay');
        this.clearIntervals();
        
        // Reload the page to reflect any changes
        window.location.reload();
      },
  
      forceClose() {
        this.hide();
      },
  
      clearIntervals() {
        if (this.paymentCheckInterval) {
          clearInterval(this.paymentCheckInterval);
          this.paymentCheckInterval = null;
        }
        if (this.paymentCheckTimeout) {
          clearTimeout(this.paymentCheckTimeout);
          this.paymentCheckTimeout = null;
        }
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
          this.countdownInterval = null;
        }
      },
  
      async confirmPurchase() {
        if (typeof this.onConfirm === 'function') {
          this.loading = true;
          try {
            // Call the onConfirm callback if it exists
            // It should return a promise that resolves when the payment is initiated
            await this.onConfirm(this.addon);
            
            // Start the payment status polling with countdown
            this.waitingForPayment = true;
            this.loading = false;
            this.countdown = 15 * 60;
            
            // Start polling for subscription status which will handle the countdown
            this.pollSubscriptionStatus();
  
          } catch (error) {
            this.loading = false;
            console.error('Purchase confirmation error:', error);
            this.$toast.error('Failed to process payment. Please try again.');
          }
        } else {
          // If no onConfirm callback, just start polling for subscription status
          this.waitingForPayment = true;
          this.loading = false;
          this.countdown = 15 * 60;
          this.pollSubscriptionStatus();
        }
      },
  
      async pollSubscriptionStatus() {
        const POLL_INTERVAL = 1000; // 1 second for countdown
        const POLL_FREQUENCY = 5000; // 5 seconds between API calls
        const MAX_POLL_TIME = 15 * 60 * 1000; // 15 minutes max
        
        let lastPollTime = 0;
        let isProcessing = false;
        
        // Clear any existing intervals first
        this.clearIntervals();

        // Get the authentication token
        const token = document.querySelector('meta[name="api-token"]')?.getAttribute('content') || 
                     localStorage.getItem('auth_token');
        
        if (!token) {
          console.error('Authentication token not found');
          this.$toast.error('Authentication error. Please refresh the page and try again.');
          this.waitingForPayment = false;
          return;
        }

        // Create a cleanup function
        const cleanup = () => {
          this.clearIntervals();
          isProcessing = false;
        };

        // Start the combined interval for both countdown and polling
        this.paymentCheckInterval = setInterval(async () => {
          // Update countdown every second
          if (this.countdown > 0) {
            this.countdown--;
          } else {
            // If countdown reaches zero, handle timeout
            cleanup();
            this.waitingForPayment = false;
            this.$toast.warning('Payment confirmation timed out. Please check your email or transaction history.');
            this.$emit('payment-timeout');
            return;
          }
          
          // Only poll the server every 5 seconds and ensure we're not already processing
          const now = Date.now();
          if (!isProcessing && (now - lastPollTime >= POLL_FREQUENCY || lastPollTime === 0)) {
            isProcessing = true;
            lastPollTime = now;
            
            try {
              console.debug(`[${new Date().toISOString()}] Checking subscription status...`);
              
              // Check subscription status using the API endpoint
              const params = { stripe_price_id: this.addon?.stripe_price_id };
              
              const response = await axios.get('/api/subscription/status', {
                params,
                headers: {
                  'Accept': 'application/json',
                  'X-Requested-With': 'XMLHttpRequest',
                  'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                  'Authorization': `Bearer ${token}`
                },
                withCredentials: true,
                timeout: 10000 // 10 second timeout
              });
              
              console.debug('Subscription status response:', response.data);
              
              // Check if there's an active subscription
              if (response.data.has_active_subscription && Array.isArray(response.data.subscriptions)) {
                const activeSubscription = response.data.subscriptions.find(
                  sub => sub.stripe_price_id === this.addon.stripe_price_id && 
                        (sub.status === 'active' || sub.status === 'trialing')
                );
                
                if (activeSubscription) {
                  console.log('Active subscription found:', activeSubscription);
                  cleanup();
                  this.paymentStatus = 'success';
                  this.waitingForPayment = false;
                  
                  // Emit success event before showing toast and hiding modal
                  this.$emit('payment-success', {
                    subscriptionId: activeSubscription.id,
                    status: activeSubscription.status,
                    ...activeSubscription
                  });
                  
                  this.$toast.success('Payment successful!');
                  this.hide(); // This will now handle closing all modals and reloading
                  return;
                }
              }
              
            } catch (error) {
              console.error('Error checking subscription status:', error);
              
              // Only show error to user if it's not a network error or timeout
              if (error.code !== 'ECONNABORTED' && error.code !== 'ERR_NETWORK') {
                this.$toast.error('Error verifying payment status. Please try again.');
              }
              
              // Don't stop polling on error, just log it and continue
            } finally {
              isProcessing = false;
            }
          }
        }, POLL_INTERVAL);
        
        // Set a timeout to clear the interval after max time
        this.paymentCheckTimeout = setTimeout(() => {
          if (this.paymentStatus !== 'success') {
            cleanup();
            this.waitingForPayment = false;
            this.$toast.warning('Payment confirmation timed out. Please check your email or transaction history.');
            this.$emit('payment-timeout');
          }
        }, MAX_POLL_TIME);
      },
    },
  
    beforeDestroy() {
      this.clearIntervals();
    },
    
    // Clean up when modal is closed
    beforeRouteLeave(to, from, next) {
      this.clearIntervals();
      next();
    }
  };
  </script>
  